import _extends from '@babel/runtime/helpers/extends';
import React, { useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import IconCross from './IconCross.js';
import { a as addClassNames, c as classNamePropTypes } from './classNames-1ed461aa.js';
import { a as addSpacingClasses, m as marginPropTypes } from './spacing-0f39ba4a.js';

function Toast({
  autoDismiss,
  autoDismissTime,
  children,
  className,
  dismissButtonText,
  margin,
  onDismiss,
  position,
  renderNode,
  ...otherProps
}) {
  let cssClasses = ['st_toast', `st_toast st_toast--${position}`];
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addSpacingClasses({
    margin,
    cssClasses
  });
  const dismissTimer = useRef(false);

  // set timer for autodismiss
  useEffect(() => {
    if (autoDismiss) {
      dismissTimer.current = setTimeout(() => {
        if (onDismiss) onDismiss();
      }, autoDismissTime * 1000);
      return () => dismissTimer.current && clearTimeout(dismissTimer.current);
    }
    return undefined;
  }, [autoDismiss, onDismiss, autoDismissTime]);

  // handle when X is clicked to manually close Modal
  const handleDismiss = useCallback(() => {
    clearTimeout(dismissTimer.current);
    if (onDismiss) onDismiss();
  }, [onDismiss]);
  const content = /*#__PURE__*/React.createElement("div", _extends({
    className: cssClasses.join(' '),
    role: "status",
    "aria-live": "polite",
    "aria-atomic": "true"
  }, otherProps), /*#__PURE__*/React.createElement("div", {
    className: "st_toast__content"
  }, children), onDismiss && /*#__PURE__*/React.createElement("button", {
    className: "st_toast__dismiss",
    onClick: handleDismiss,
    type: "button",
    "aria-label": "Close"
  }, dismissButtonText || /*#__PURE__*/React.createElement(IconCross, {
    alt: "dismiss",
    size: "mlg"
  })));
  if (!renderNode) return content;
  return /*#__PURE__*/ReactDOM.createPortal(content, renderNode);
}
Toast.propTypes = {
  /** whether to fire onDismiss automatically after X seconds */
  autoDismiss: PropTypes.bool,
  /** how many seconds to wait before firing onDismiss automatically */
  autoDismissTime: PropTypes.number,
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** text content for dismiss button, if any */
  dismissButtonText: PropTypes.string,
  /** margin for component, if any */
  margin: marginPropTypes,
  /** callback when Toast is dismissed, either via the button, or after timeout */
  onDismiss: PropTypes.func,
  /** position of toast on bottom of page */
  position: PropTypes.oneOf(['left', 'center', 'right']),
  /** node where toast should be rendered */
  renderNode: PropTypes.any
};
Toast.defaultProps = {
  autoDismiss: false,
  autoDismissTime: 3,
  className: null,
  dismissButtonText: null,
  margin: null,
  onDismiss: null,
  position: 'right',
  renderNode: null
};

export { Toast as T };
